import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useDiscardMembershipDraft,
  useMembershipDraftQuery,
  useSaveMembershipDraft,
} from '../queries';
import type { MembershipDraftPayload, MembershipDraftSnapshot } from '../api/membership.api';

const DEFAULT_DEBOUNCE_MS = 1500;

export type MembershipDraftHandle = {
  /** Best-effort current draft snapshot from the server (may be `null`). */
  snapshot: MembershipDraftSnapshot | null;
  /** Local working payload — what the form is actively editing. */
  payload: MembershipDraftPayload;
  /** Highest step the merchant has reached so far. */
  stepReached: number;
  isLoading: boolean;
  isSaving: boolean;
  /** Last successful save timestamp (ms since epoch). `null` until the first save lands. */
  lastSavedAt: number | null;
  /**
   * Merge fields into the local payload. Triggers a debounced server save.
   * Pass an optional `stepReached` to bump the recorded progress.
   */
  patch: (partial: MembershipDraftPayload, stepReached?: number) => void;
  /** Force-flush any pending debounced save immediately and resolve. */
  flush: () => Promise<void>;
  /** Discard the active draft (soft-delete server-side, clear local state). */
  discard: () => Promise<void>;
};

/**
 * Resumable, debounced wizard draft. Loads any existing draft on mount,
 * tracks local edits, and persists changes on a 1.5 s debounce. Callers
 * can `flush()` before publish or `discard()` on explicit cancel.
 */
export const useMembershipDraft = (
  options: { enabled?: boolean; debounceMs?: number } = {}
): MembershipDraftHandle => {
  const enabled = options.enabled ?? true;
  const debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE_MS;

  const draftQuery = useMembershipDraftQuery(enabled);
  const saveMutation = useSaveMembershipDraft();
  const discardMutation = useDiscardMembershipDraft();

  const [payload, setPayload] = useState<MembershipDraftPayload>({});
  const [stepReached, setStepReached] = useState(1);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const hydratedRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  const pendingRef = useRef<{ payload: MembershipDraftPayload; stepReached: number } | null>(null);

  // Hydrate local state from the server-loaded draft exactly once.
  useEffect(() => {
    if (hydratedRef.current) return;
    if (draftQuery.isLoading) return;
    if (draftQuery.data) {
      setPayload(draftQuery.data.payload ?? {});
      setStepReached(draftQuery.data.stepReached || 1);
      if (draftQuery.data.updatedAt) {
        const ts = new Date(draftQuery.data.updatedAt).getTime();
        if (!Number.isNaN(ts)) setLastSavedAt(ts);
      }
    }
    hydratedRef.current = true;
  }, [draftQuery.isLoading, draftQuery.data]);

  const flush = useCallback(async () => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    const result = await saveMutation.mutateAsync(pending);
    if (result?.updatedAt) {
      const ts = new Date(result.updatedAt).getTime();
      if (!Number.isNaN(ts)) setLastSavedAt(ts);
    } else {
      setLastSavedAt(Date.now());
    }
  }, [saveMutation]);

  const scheduleSave = useCallback(
    (next: MembershipDraftPayload, nextStep: number) => {
      pendingRef.current = { payload: next, stepReached: nextStep };
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
      }
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        flush();
      }, debounceMs);
    },
    [debounceMs, flush]
  );

  const patch = useCallback(
    (partial: MembershipDraftPayload, nextStep?: number) => {
      setPayload((current) => {
        const merged = { ...current, ...partial };
        setStepReached((currentStep) => {
          const resolvedStep = Math.max(currentStep, nextStep ?? currentStep);
          scheduleSave(merged, resolvedStep);
          return resolvedStep;
        });
        return merged;
      });
    },
    [scheduleSave]
  );

  const discard = useCallback(async () => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    pendingRef.current = null;
    await discardMutation.mutateAsync();
    setPayload({});
    setStepReached(1);
    setLastSavedAt(null);
  }, [discardMutation]);

  // Flush any pending save on unmount so the merchant doesn't lose work
  // when they navigate away.
  useEffect(() => {
    return () => {
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (pendingRef.current) {
        // Fire-and-forget — best effort on unmount.
        saveMutation.mutate(pendingRef.current);
        pendingRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    snapshot: draftQuery.data ?? null,
    payload,
    stepReached,
    isLoading: draftQuery.isLoading,
    isSaving: saveMutation.isPending,
    lastSavedAt,
    patch,
    flush,
    discard,
  };
};
